import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 量子计算基础可视化
 * 
 * 核心效果：
 * 1. 量子比特概念展示
 * 2. 叠加态动画
 * 3. 量子纠缠可视化
 * 4. 应用场景演示
 * 
 * 配色方案：
 * - 背景：深黑 #0F1419
 * - 主色：暖橙 #FF6B35
 * - 辅色：冷青 #4ECDC4
 */

export const QuantumComputingBasics: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 动画时间轴（总 240 帧）
  const section1End = 60;   // 量子比特概念
  const section2End = 140;  // 叠加态
  const section3End = 200;  // 量子纠缠
  const section4End = 240;  // 应用

  // 标题动画
  const titleOpacity = interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' });
  const titleScale = spring({
    frame,
    from: 0.8,
    to: 1,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  // 量子比特旋转
  const qubitRotation = interpolate(frame, [0, 240], [0, 360]);

  // 叠加态粒子
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: Math.sin(i * 0.8) * 150 + 400,
    y: Math.cos(i * 0.6) * 150 + 300,
    size: Math.random() * 8 + 4,
    speed: Math.random() * 2 + 1,
  }));

  // 当前章节
  const getCurrentSection = () => {
    if (frame < section1End) return 1;
    if (frame < section2End) return 2;
    if (frame < section3End) return 3;
    return 4;
  };

  const currentSection = getCurrentSection();

  // 章节标题
  const sections = [
    { title: '量子比特 Qubit', icon: '⚛️', color: '#FF6B35' },
    { title: '量子叠加态 Superposition', icon: '🌀', color: '#4ECDC4' },
    { title: '量子纠缠 Entanglement', icon: '🔗', color: '#9B59B6' },
    { title: '实际应用 Applications', icon: '💡', color: '#F39C12' },
  ];

  // 应用场景
  const applications = [
    { icon: '🔐', title: '密码学', desc: '量子加密' },
    { icon: '💊', title: '药物研发', desc: '分子模拟' },
    { icon: '🌐', title: '优化问题', desc: '物流调度' },
    { icon: '🤖', title: 'AI 加速', desc: '机器学习' },
  ];

  return (
    <AbsoluteFill style={{
      background: '#0F1419',
      fontFamily: 'Arial, sans-serif',
      color: '#FFFFFF',
      padding: 60,
    }}>
      {/* 标题 */}
      <div style={{
        position: 'absolute',
        top: 40,
        left: 0,
        right: 0,
        textAlign: 'center',
        opacity: titleOpacity,
        transform: `scale(${titleScale})`,
      }}>
        <h1 style={{
          fontSize: 80,
          fontWeight: 700,
          background: 'linear-gradient(90deg, #FF6B35, #4ECDC4)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          margin: 0,
        }}>
          量子计算基础
        </h1>
        <p style={{
          fontSize: 32,
          color: '#8B92A5',
          marginTop: 10,
        }}>
          Quantum Computing Basics
        </p>
      </div>

      {/* 章节导航 */}
      <div style={{
        position: 'absolute',
        top: 300,
        left: 60,
        width: 280,
      }}>
        {sections.map((section, i) => {
          const isActive = currentSection === i + 1;
          const isPast = currentSection > i + 1;
          
          return (
            <div key={i} style={{
              padding: '15px 20px',
              marginBottom: 15,
              background: isActive 
                ? `${section.color}20`
                : isPast 
                  ? 'rgba(78, 205, 196, 0.1)'
                  : 'rgba(26, 31, 46, 0.5)',
              borderRadius: 12,
              border: isActive 
                ? `2px solid ${section.color}`
                : '2px solid transparent',
              opacity: interpolate(
                frame,
                [i * 20, i * 20 + 20],
                [0, 1],
                { extrapolateRight: 'clamp' }
              ),
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}>
              <span style={{ fontSize: 34 }}>{section.icon}</span>
              <div>
                <div style={{
                  fontSize: 26,
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? section.color : '#FFFFFF',
                }}>
                  {section.title}
                </div>
                {/* {isActive && (
                  <div style={{
                    fontSize: 22,
                    color: section.color,
                    marginTop: 4,
                  }}>
                    ● 当前章节
                  </div>
                )} */}
              </div>
            </div>
          );
        })}
      </div>

      {/* 主内容区域 */}
      <div style={{
        position: 'absolute',
        top: 300,
        left: 380,
        right: 60,
        bottom: 100,
      }}>
        {/* 章节 1: 量子比特 */}
        {currentSection === 1 && (
          <div style={{
            opacity: interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' }),
          }}>
            <div style={{
              fontSize: 48,
              fontWeight: 700,
              marginBottom: 30,
              color: '#FF6B35',
            }}>
              ⚛️ 什么是量子比特？
            </div>
            
            <div style={{
              display: 'flex',
              gap: 40,
              marginBottom: 40,
            }}>
              {/* 经典比特 */}
              <div style={{
                flex: 1,
                background: '#1A1F2E',
                borderRadius: 15,
                padding: 30,
                border: '2px solid #2A2F3E',
              }}>
                <div style={{
                  fontSize: 28,
                  fontWeight: 700,
                  marginBottom: 20,
                  color: '#8B92A5',
                }}>
                  经典比特
                </div>
                <div style={{
                  display: 'flex',
                  gap: 20,
                  justifyContent: 'center',
                  marginBottom: 20,
                }}>
                  <div style={{
                    width: 80,
                    height: 80,
                    background: '#2A2F3E',
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 40,
                    border: '3px solid #4ECDC4',
                  }}>
                    0
                  </div>
                  <div style={{
                    width: 80,
                    height: 80,
                    background: '#2A2F3E',
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 40,
                    border: '3px solid #FF6B35',
                  }}>
                    1
                  </div>
                </div>
                <div style={{
                  fontSize: 24,
                  color: '#8B92A5',
                  textAlign: 'center',
                }}>
                  只能是 0 或 1
                </div>
              </div>

              {/* 量子比特 */}
              <div style={{
                flex: 1,
                background: '#1A1F2E',
                borderRadius: 15,
                padding: 30,
                border: '2px solid #4ECDC4',
              }}>
                <div style={{
                  fontSize: 28,
                  fontWeight: 700,
                  marginBottom: 20,
                  color: '#4ECDC4',
                }}>
                  量子比特
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: 20,
                }}>
                  <div style={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: `conic-gradient(from ${qubitRotation}deg, #4ECDC4, #FF6B35, #9B59B6, #4ECDC4)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 30px rgba(78, 205, 196, 0.5)',
                  }}>
                    <div style={{
                      width: 60,
                      height: 60,
                      background: '#0F1419',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 28,
                      fontWeight: 700,
                    }}>
                      |ψ⟩
                    </div>
                  </div>
                </div>
                <div style={{
                  fontSize: 24,
                  color: '#4ECDC4',
                  textAlign: 'center',
                }}>
                  可以同时是 0 和 1
                </div>
              </div>
            </div>

            {/* 公式说明 */}
            <div style={{
              background: '#1A1F2E',
              borderRadius: 15,
              padding: 25,
              border: '2px solid #2A2F3E',
            }}>
              <div style={{
                fontSize: 34,
                fontFamily: 'Times New Roman, serif',
                textAlign: 'center',
                color: '#4ECDC4',
              }}>
                |ψ⟩ = α|0⟩ + β|1⟩
              </div>
              <div style={{
                fontSize: 24,
                color: '#8B92A5',
                textAlign: 'center',
                marginTop: 15,
              }}>
                α 和 β 是复数概率幅
              </div>
            </div>
          </div>
        )}

        {/* 章节 2: 叠加态 */}
        {currentSection === 2 && (
          <div style={{
            opacity: interpolate(frame, [section1End, section1End + 20], [0, 1], { extrapolateRight: 'clamp' }),
          }}>
            <div style={{
              fontSize: 48,
              fontWeight: 700,
              marginBottom: 30,
              color: '#4ECDC4',
            }}>
              🌀 量子叠加态
            </div>

            {/* 叠加态可视化 */}
            <div style={{
              position: 'relative',
              height: 400,
              background: '#1A1F2E',
              borderRadius: 15,
              border: '2px solid #2A2F3E',
              overflow: 'hidden',
            }}>
              {particles.map((particle) => {
                const particleFrame = frame - section1End;
                const x = particle.x + Math.sin(particleFrame * 0.02 * particle.speed) * 100;
                const y = particle.y + Math.cos(particleFrame * 0.02 * particle.speed) * 100;
                const opacity = interpolate(
                  particleFrame,
                  [0, 30],
                  [0, 0.8],
                  { extrapolateRight: 'clamp' }
                );

                return (
                  <div key={particle.id} style={{
                    position: 'absolute',
                    left: x,
                    top: y,
                    width: particle.size,
                    height: particle.size,
                    background: particle.id % 2 === 0 ? '#4ECDC4' : '#FF6B35',
                    borderRadius: '50%',
                    opacity,
                    boxShadow: particle.id % 2 === 0 
                      ? '0 0 15px rgba(78, 205, 196, 0.5)'
                      : '0 0 15px rgba(255, 107, 53, 0.5)',
                  }} />
                );
              })}
              
              {/* 中心文字 */}
              <div style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: 36,
                fontWeight: 700,
                textAlign: 'center',
                color: '#FFFFFF',
              }}>
                <div style={{ color: '#4ECDC4' }}>|0⟩ + |1⟩</div>
                <div style={{
                  fontSize: 24,
                  color: '#8B92A5',
                  marginTop: 10,
                }}>
                  同时处于两种状态
                </div>
              </div>
            </div>

            {/* 优势说明 */}
            <div style={{
              marginTop: 30,
              display: 'flex',
              gap: 20,
            }}>
              {[
                { text: '并行计算', icon: '⚡' },
                { text: '指数加速', icon: '📈' },
                { text: '量子优势', icon: '🎯' },
              ].map((item, i) => (
                <div key={i} style={{
                  flex: 1,
                  background: 'rgba(78, 205, 196, 0.1)',
                  borderRadius: 12,
                  padding: 20,
                  border: '2px solid rgba(78, 205, 196, 0.3)',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>{item.icon}</div>
                  <div style={{ fontSize: 24, fontWeight: 700 }}>{item.text}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 章节 3: 量子纠缠 */}
        {currentSection === 3 && (
          <div style={{
            opacity: interpolate(frame, [section2End, section2End + 20], [0, 1], { extrapolateRight: 'clamp' }),
          }}>
            <div style={{
              fontSize: 48,
              fontWeight: 700,
              marginBottom: 30,
              color: '#9B59B6',
            }}>
              🔗 量子纠缠
            </div>

            {/* 纠缠可视化 */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 80,
              marginBottom: 40,
            }}>
              {/* 量子 1 */}
              <div style={{
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: `conic-gradient(from ${qubitRotation}deg, #9B59B6, #3498DB, #9B59B6)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 40px rgba(155, 89, 182, 0.6)',
              }}>
                <div style={{
                  width: 80,
                  height: 80,
                  background: '#0F1419',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 32,
                }}>
                  Q1
                </div>
              </div>

              {/* 纠缠线 */}
              <div style={{
                width: 200,
                height: 4,
                background: `linear-gradient(90deg, #9B59B6, #3498DB, #9B59B6)`,
                opacity: interpolate(frame, [section2End, section2End + 40], [0, 1], { extrapolateRight: 'clamp' }),
              }} />

              {/* 量子 2 */}
              <div style={{
                width: 150,
                height: 150,
                borderRadius: '50%',
                background: `conic-gradient(from ${-qubitRotation}deg, #3498DB, #9B59B6, #3498DB)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 40px rgba(52, 152, 219, 0.6)',
              }}>
                <div style={{
                  width: 80,
                  height: 80,
                  background: '#0F1419',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 32,
                }}>
                  Q2
                </div>
              </div>
            </div>

            {/* 说明 */}
            <div style={{
              background: '#1A1F2E',
              borderRadius: 15,
              padding: 30,
              border: '2px solid #9B59B6',
              marginTop: 70,
            }}>
              <div style={{
                fontSize: 28,
                lineHeight: '36px',
                color: '#FFFFFF',
              }}>
                <div style={{ marginBottom: 15 }}>
                  <strong style={{ color: '#9B59B6' }}>幽灵般的超距作用：</strong>
                  两个纠缠的量子，无论相距多远，改变其中一个，另一个立即发生相应变化
                </div>
                <div style={{ color: '#8B92A5' }}>
                  这使得量子计算机能够以全新的方式处理信息
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 章节 4: 应用 */}
        {currentSection === 4 && (
          <div style={{
            opacity: interpolate(frame, [section3End, section3End + 20], [0, 1], { extrapolateRight: 'clamp' }),
          }}>
            <div style={{
              fontSize: 48,
              fontWeight: 700,
              marginTop: 0,
              marginBottom: 30,
              color: '#F39C12',
            }}>
              💡 实际应用
            </div>

            {/* 应用场景网格 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 25,
            }}>
              {applications.map((app, i) => {
                const cardOpacity = interpolate(
                  frame,
                  [section3End + i * 10, section3End + i * 10 + 20],
                  [0, 1],
                  { extrapolateRight: 'clamp' }
                );
                const cardScale = spring({
                  frame: frame - section3End - i * 10,
                  from: 0.8,
                  to: 1,
                  fps,
                  config: { damping: 15, stiffness: 150 },
                });

                return (
                  <div key={i} style={{
                    background: '#1A1F2E',
                    borderRadius: 15,
                    padding: 30,
                    border: '2px solid #2A2F3E',
                    opacity: cardOpacity,
                    transform: `scale(${cardScale})`,
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      marginBottom: 10,
                    }}>
                      <span style={{ fontSize: 48 }}>{app.icon}</span>
                      <span style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: '#F39C12',
                      }}>
                        {app.title}
                      </span>
                    </div>
                    <div style={{
                      fontSize: 26,
                      color: '#8B92A5',
                    }}>
                      {app.desc}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AbsoluteFill>
  );
};
